import { type PageType } from '@blaze-shared/api-contracts-domain';

import { type CanvasElementOverrides } from './canvas-element';
import {
  type CanvasElementId,
  type CanvasElementType,
  type CanvasNamespaceId,
} from './canvas-element-id';
import { type CanvasElementDefinitions } from './definition';
import { type CanvasElementProperties } from './props';

/**
 * Used to extend the CanvasElement model with the `slots` property.
 *
 * slots: {
 *   ids: [...ids of slots],
 *   [id_of_slot1]: {
 *     children: [...ids of elements in slot in order of appearance],
 *     sizing: Sizingdata
 *   },
 *   [id_of_slot2]: {...},
 *   [id_of_slotN]: {...}
 * }
 */
export interface WithSlots<T extends string> {
  slots: Slots<T>;
}

export type Slots<T extends string> = Omit<Partial<Record<T, SlotData>>, 'ids'> & {
  ids: Exclude<T, 'ids'>[]; // 'ids' is forbidden
};

export type ColumnId = `${number}`;
export type WithColumns = WithSlots<ColumnId>;

export const NS_HOST_TYPE_PREFIX = 'nsHostType';
export type NsHostTypeSlotId = `${typeof NS_HOST_TYPE_PREFIX}:${PageType}`;
export type WithNsHostTypeSlot = WithSlots<NsHostTypeSlotId>;

export interface SlotData<Props extends CanvasElementProperties = CanvasElementProperties> {
  children: CanvasElementId[];
  /**
   * If defined, the slot will host a lazily loaded nested layout, the children will overriden by loaded layout
   */
  hosting?: SlotHost;
  sizing?: SlotSizing;
  alignment?: SlotAlignment;
  props?: Props;
  overrides?: CanvasElementOverrides<Props>;
}

/** Provides details about the source of the nested layout */
export interface SlotHost {
  /** Namespace Id to be hosted */
  ns?: CanvasNamespaceId;
}

export interface SlotSizing {
  mode?: SlotSizingMode;
  width?: number;
}

export interface SlotAlignment {
  /**
   * Horizontal alignment of the content inside of the slot
   * Default is 'left'
   */
  horizontal: 'left' | 'center' | 'right';
  /**
   * Vertical alignment of the content inside of the slot
   * Default is 'top'
   */
  vertical: 'top' | 'middle' | 'bottom';
  /**
   * BL-3762 Sync line alignment with vertical alignment.
   * Added new switch to not break existing layouts.
   * It should be set to true everywhere we create / change slot alignment.
   */
  syncVerticalLineAlignment?: boolean;
}

export type SlotSizingMode = 'percent' | 'fit-content' | 'auto';

export type SlotsOf<T extends CanvasElementType> = SlotsFor<CanvasElementDefinitions[T]>;
export type SlotsFor<T> = T extends { slots: unknown } ? T['slots'] : undefined;

/**
 * Returns a union with all available slot ids for the provided element
 *
 * It is taken from the element's definition
 */
export type SlotIdOf<T extends CanvasElementType, Def = CanvasElementDefinitions[T]> = Def extends {
  slots: { ids: string[] };
}
  ? Def['slots']['ids'][number]
  : never;

export type SlotId = SlotIdOf<CanvasElementType>;
